import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import Button from "../../../shared/ui/Button";
import EnterpriseFilterSelect from "../../../shared/components/EnterpriseFilterSelect";
import {
  getRoleOptions,
  getAccountStatusOptions,
  fetchAdminUsers,
  formatAdminError,
  updateAdminUserRole,
  updateAdminUserAccountStatus,
  impersonateAdminUser,
} from "../adminApi";
import AdminRegisterUserModal from "../components/AdminRegisterUserModal";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import {
  getStoredIsSuperuser,
  persistAuthUser,
  applyAuthResponse,
  getCurrentUser,
  USER_ROLE,
} from "../../../lib/authSession";
import { applyAccountStatusToSession } from "../../../lib/accountStatus";
import { getPostLoginPath } from "../../../lib/authRoutes";
import { useTranslation } from "../../../i18n";

const PAGE_SIZE = 20;

const ROLE_FILTER_OPTIONS = (t) => [
  { value: "", label: t("common.all") },
  ...getRoleOptions(t),
];

const ACCOUNT_STATUS_FILTER_OPTIONS = (t) => [
  { value: "", label: t("common.all") },
  ...getAccountStatusOptions(t),
];

const TABLE_SELECT_BUTTON =
  "flex h-9 w-full items-center justify-between gap-2 rounded-lg border border-outline-variant/40 bg-surface-container-lowest px-2 text-body-md text-on-surface ring-0 transition-all hover:ring-secondary/40 focus:outline-none focus:ring-2 focus:ring-secondary";

function formatDateDe(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function initialCanManage() {
  if (getStoredIsSuperuser()) return true;
  const u = getCurrentUser();
  return Boolean(u?.role === USER_ROLE.ADMIN);
}

const AdminUsersPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const roleOptions = useMemo(() => getRoleOptions(t), [t]);
  const accountStatusOptions = useMemo(() => getAccountStatusOptions(t), [t]);
  const roleFilterOptions = useMemo(() => ROLE_FILTER_OPTIONS(t), [t]);
  const accountFilterOptions = useMemo(() => ACCOUNT_STATUS_FILTER_OPTIONS(t), [t]);
  const [canManage, setCanManage] = useState(initialCanManage);
  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [updatingField, setUpdatingField] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [impersonatingId, setImpersonatingId] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [accountStatusFilter, setAccountStatusFilter] = useState("");

  const currentUserId = getCurrentUser()?.id;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminUsers({
        page,
        pageSize: PAGE_SIZE,
        search: search || undefined,
        role: roleFilter || undefined,
        accountStatus: accountStatusFilter || undefined,
      });
      setUsers(data.results);
      setTotalCount(data.count);
    } catch (err) {
      setError(formatAdminError(err));
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, roleFilter, accountStatusFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/api/users/profile/", { auth: true })
      .then((profile) => {
        if (cancelled) return;
        persistAuthUser(profile);
        setCanManage(Boolean(profile.is_superuser) || profile.role === "admin");
      })
      .catch(() => {
        if (!cancelled) setCanManage(initialCanManage());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRoleChange = async (userId, role) => {
    setUpdatingId(userId);
    setUpdatingField("role");
    setError("");
    try {
      const updated = await updateAdminUserRole(userId, role);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, ...updated } : u))
      );
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setUpdatingId(null);
      setUpdatingField(null);
    }
  };

  const handleAccountStatusChange = async (userId, accountStatus) => {
    setUpdatingId(userId);
    setUpdatingField("accountStatus");
    setError("");
    try {
      const updated = await updateAdminUserAccountStatus(userId, accountStatus);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, ...updated } : u))
      );
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setUpdatingId(null);
      setUpdatingField(null);
    }
  };

  const handleImpersonate = async (user) => {
    setImpersonatingId(user.id);
    setError("");
    try {
      const data = await impersonateAdminUser(user.id);
      applyAuthResponse(data);
      if (data?.user) {
        applyAccountStatusToSession(data.user);
      }
      // Prefetch this client's connected platforms before landing in their dashboard.
      const { hydrateConnectionsFromServer } = await import(
        "../../../client/dashboard/platformConnectionsStorage"
      );
      await hydrateConnectionsFromServer(data?.user?.company?.id);
      navigate(getPostLoginPath(data?.user?.role || "user"), { replace: true });
    } catch (err) {
      setError(formatAdminError(err));
    } finally {
      setImpersonatingId(null);
    }
  };

  const canImpersonateRow = useCallback(
    (u) => {
      if (!canManage) return false;
      if (u.isSuperuser) return false;
      if (u.id === currentUserId) return false;
      // Clients: any admin may impersonate. Other admins: superuser only (matches API).
      if (u.role === USER_ROLE.CLIENT || u.role === "user") return true;
      if (u.role === USER_ROLE.ADMIN || u.role === "admin") {
        return getStoredIsSuperuser();
      }
      return false;
    },
    [canManage, currentUserId]
  );

  const filterSummary = useMemo(() => {
    const parts = [];
    if (search) parts.push(t("adminTable.filterSummarySearch", { query: search }));
    if (roleFilter) {
      parts.push(
        getRoleOptions(t).find((o) => o.value === roleFilter)?.label || roleFilter
      );
    }
    if (accountStatusFilter) {
      parts.push(
        getAccountStatusOptions(t).find((o) => o.value === accountStatusFilter)
          ?.label || accountStatusFilter
      );
    }
    return parts.join(" · ");
  }, [search, roleFilter, accountStatusFilter, t]);

  return (
    <div className="bg-background font-body-md text-on-surface">
      <div className="mx-auto max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <header className="mb-stack-lg flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-headline-lg text-headline-lg text-primary">
              {t("admin.usersTitle")}
            </h1>
            <p className="mt-2 max-w-2xl text-body-md text-on-surface-variant">
              {t("adminTable.usersSubtitleExtended")}
            </p>
          </div>
          {canManage && getStoredIsSuperuser() ? (
            <Button
              type="button"
              variant="teal"
              className="!h-11 !w-auto shrink-0 !rounded-full !px-6"
              onClick={() => setRegisterOpen(true)}
            >
              {t("adminTable.registerUser")}
            </Button>
          ) : null}
        </header>

        <AdminRegisterUserModal
          open={registerOpen}
          onClose={() => setRegisterOpen(false)}
          onRegistered={() => loadUsers()}
        />

        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-4 shadow-soft sm:flex-row sm:flex-wrap sm:items-end">
          <label className="min-w-[200px] flex-1">
            <span className="mb-1 block font-label-sm text-on-surface-variant">
              {t("adminTable.searchLabel")}
            </span>
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t("admin.searchPlaceholder")}
              className="h-10 w-full rounded-lg border border-outline-variant/40 bg-surface-container-low px-3 text-body-md text-on-surface outline-none focus:ring-2 focus:ring-secondary"
            />
          </label>
          <label className="min-w-[160px]">
            <span className="mb-1 block font-label-sm text-on-surface-variant">
              {t("admin.role")}
            </span>
            <EnterpriseFilterSelect
              value={roleFilter}
              onChange={(value) => {
                setRoleFilter(value);
                setPage(1);
              }}
              options={roleFilterOptions}
              minWidth="160px"
              fullWidth={false}
              buttonClassName={TABLE_SELECT_BUTTON}
              ariaLabel={t("admin.filterRole")}
            />
          </label>
          <label className="min-w-[160px]">
            <span className="mb-1 block font-label-sm text-on-surface-variant">
              {t("admin.account")}
            </span>
            <EnterpriseFilterSelect
              value={accountStatusFilter}
              onChange={(value) => {
                setAccountStatusFilter(value);
                setPage(1);
              }}
              options={accountFilterOptions}
              minWidth="160px"
              fullWidth={false}
              buttonClassName={TABLE_SELECT_BUTTON}
              ariaLabel={t("admin.filterAccount")}
            />
          </label>
        </div>

        {!loading && totalCount > 0 ? (
          <p className="mb-2 text-label-sm font-medium text-on-surface-variant">
            {t("admin.totalUsers", { count: totalCount })}
            {totalPages > 1 ? ` · ${t("admin.pageOf", { page, total: totalPages })}` : ""}
          </p>
        ) : null}

        {filterSummary ? (
          <p className="mb-3 text-label-sm text-on-surface-variant">{filterSummary}</p>
        ) : null}

        {error && (
          <p className="mb-4 rounded-xl border border-error/30 bg-error-container px-4 py-2 text-body-md text-on-error-container">
            {error}
          </p>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-outline-variant/30 bg-surface-container-lowest py-16">
            <MaterialIcon name="sync" className="animate-spin text-[36px] text-secondary" aria-hidden />
            <p className="mt-4 text-body-md text-on-surface-variant">{t("admin.loadingUsers")}</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest shadow-soft">
            <div className="overflow-x-auto">
              <table className="min-w-full whitespace-nowrap text-left text-body-md">
                <thead className="border-b border-outline-variant/30 bg-surface-container-low text-label-sm font-semibold uppercase text-on-surface-variant">
                  <tr>
                    <th className="px-4 py-3">{t("adminTable.name")}</th>
                    <th className="px-4 py-3">{t("adminTable.email")}</th>
                    <th className="px-4 py-3">{t("adminTable.company")}</th>
                    <th className="px-4 py-3">{t("admin.role")}</th>
                    <th className="px-4 py-3">{t("admin.account")}</th>
                    <th className="px-4 py-3">{t("adminTable.registered")}</th>
                    <th className="px-4 py-3 text-right">{t("adminTable.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-outline-variant/20 last:border-0"
                    >
                      <td className="px-4 py-3 font-medium text-primary">
                        <div className="inline-flex items-center gap-2">
                          <Link
                            to={`/dashboard/admin/users/${u.id}`}
                            className="hover:text-secondary hover:underline"
                          >
                            {u.fullName}
                          </Link>
                          {u.isSuperuser ? (
                            <span className="shrink-0 rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-on-primary">
                              {t("adminTable.superLabel")}
                            </span>
                          ) : null}
                          {u.isAccountOwner && u.role === "user" && !u.isSuperuser ? (
                            <span className="shrink-0 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200">
                              {t("adminUserDetail.ownerBadge")}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-on-surface-variant">{u.email}</td>
                      <td className="px-4 py-3 text-on-surface-variant">
                        <div className="inline-flex items-center gap-2">
                          <span>{u.companyName || "—"}</span>
                          {u.memberCount > 0 ? (
                            <span className="text-[12px] text-outline">
                              ({t("adminUserDetail.membersCount", {
                                count: u.memberCount,
                              })})
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {canManage &&
                        getStoredIsSuperuser() &&
                        !u.isSuperuser ? (
                          <EnterpriseFilterSelect
                            value={u.role}
                            onChange={(value) => handleRoleChange(u.id, value)}
                            options={roleOptions}
                            disabled={
                              updatingId === u.id && updatingField === "role"
                            }
                            minWidth="130px"
                            fullWidth={false}
                            buttonClassName={TABLE_SELECT_BUTTON}
                            ariaLabel={t("adminTable.roleFor", { name: u.fullName })}
                          />
                        ) : (
                          <span className="text-on-surface-variant">
                            {u.role === "admin"
                              ? t("admin.administrator")
                              : t("admin.userRole")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {canManage &&
                        getStoredIsSuperuser() &&
                        u.role === "user" &&
                        !u.isSuperuser ? (
                          <EnterpriseFilterSelect
                            value={u.accountStatus}
                            onChange={(value) =>
                              handleAccountStatusChange(u.id, value)
                            }
                            options={accountStatusOptions}
                            disabled={
                              updatingId === u.id &&
                              updatingField === "accountStatus"
                            }
                            minWidth="130px"
                            fullWidth={false}
                            buttonClassName={TABLE_SELECT_BUTTON}
                            ariaLabel={t("adminTable.accountFor", { name: u.fullName })}
                          />
                        ) : (
                          <span className="text-on-surface-variant">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-on-surface-variant">
                        {formatDateDe(u.dateJoined)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canImpersonateRow(u) ? (
                          <button
                            type="button"
                            disabled={impersonatingId === u.id}
                            onClick={() => handleImpersonate(u)}
                            className="rounded-lg border border-secondary/30 bg-secondary/5 px-3 py-1.5 text-label-sm font-semibold text-secondary transition hover:bg-secondary/10 disabled:opacity-50"
                          >
                            {impersonatingId === u.id
                              ? t("admin.loggingIn")
                              : u.role === "user"
                                ? t("admin.openClientProfile")
                                : t("admin.loginAsAdmin")}
                          </button>
                        ) : (
                          <span className="text-label-sm text-outline">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.length === 0 && (
              <p className="px-4 py-10 text-center text-[14px] text-[#64748b]">
                {t("adminTable.noUsersFound")}
              </p>
            )}
            {(totalPages > 1 || totalCount > 0) && (
              <div className="flex flex-wrap items-center justify-center gap-4 border-t border-outline-variant/30 px-4 py-4">
                <button
                  type="button"
                  disabled={page <= 1 || totalPages <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="rounded-lg border border-outline-variant px-4 py-2 text-label-sm font-medium text-on-surface-variant transition hover:bg-surface-container-low disabled:opacity-40"
                >
                  {t("common.back")}
                </button>
                <span className="text-label-sm text-on-surface-variant">
                  {totalPages > 1
                    ? `${t("admin.pageOf", { page, total: totalPages })} (${t("admin.totalUsers", { count: totalCount })})`
                    : t("admin.totalUsers", { count: totalCount })}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages || totalPages <= 1}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded-lg border border-outline-variant px-4 py-2 text-label-sm font-medium text-on-surface-variant transition hover:bg-surface-container-low disabled:opacity-40"
                >
                  {t("auth.next")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsersPage;
